import { Component } from '@theme/component';

/**
 * Switches between the collection panels of the collection tabs section.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs - The tab buttons.
 * @property {HTMLElement[]} panels - The collection panels.
 * @property {HTMLAnchorElement[]} [links] - The "view all" links, one per panel.
 *
 * @extends Component<Refs>
 */
class CollectionTabsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];

  /**
   * @param {MouseEvent} event - The click event.
   */
  handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement)) return;

    this.select(this.refs.tabs.indexOf(tab));
  }

  /**
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeydown(event) {
    const { tabs } = this.refs;
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const next = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const previous = isRtl ? 'ArrowRight' : 'ArrowLeft';

    let target = -1;
    if (event.key === next) target = (current + 1) % tabs.length;
    if (event.key === previous) target = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = tabs.length - 1;
    if (target === -1) return;

    event.preventDefault();
    this.select(target);
    tabs[target]?.focus();
  }

  /**
   * Activates a tab and its panel.
   * @param {number} index - The tab index.
   */
  select(index) {
    const { tabs, panels, links = [] } = this.refs;
    if (index < 0 || index >= tabs.length) return;

    for (const [tabIndex, tab] of tabs.entries()) {
      const isActive = tabIndex === index;

      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      panels[tabIndex]?.classList.toggle('collection-tabs__panel--inactive', !isActive);
      links[tabIndex]?.classList.toggle('hidden', !isActive);
    }
  }
}

if (!customElements.get('collection-tabs-component')) {
  customElements.define('collection-tabs-component', CollectionTabsComponent);
}
